import { graphHooks } from '@tunasong/graph-lib/react'
import { canWriteEntity, isCollabEntity, logger } from '@tunasong/models'
import { useYDoc } from '@tunasong/plugin-lib'
import { useCurrentUser } from '@tunasong/redux'
import { isEntity, isPersisted } from '@tunasong/schemas'
import { getSeedYDocString } from '@tunasong/sync-lib'
import { AlertError, getProfileColor } from '@tunasong/ui-lib'
import { useWebSocket } from '@tunasong/ws'
import type { FC } from 'react'
import invariant from 'tiny-invariant'
import type { CollabWebSocketEditorProps } from './collab-editor-websocket.js'
import { CollabWebsocketEditor } from './collab-editor-websocket.js'

/** Inject Y.Doc from Redux */
export type CollabWebsocketEditorYDocProps = Omit<
  CollabWebSocketEditorProps,
  'doc' | 'name' | 'color' | 'webSocket' | 'awareness'
>
export const CollabWebsocketEditorYDoc: FC<CollabWebsocketEditorYDocProps> = props => {
  const { element: entity, ...restProps } = props

  invariant(isPersisted(entity) && isEntity(entity), 'CollabWebsocketEditorYDoc requires a persisted entity')

  const { userId } = useCurrentUser()

  /** Set readOnly if we do not have write access */
  const readOnly = !canWriteEntity(userId ?? '', entity)

  /**
   * A collaborative entity stored without a doc (setlists created from the New Entity dialog before
   * GH #2214) opens on the shared seed rather than crashing. Only entities with neither an inline nor
   * a stored doc are seeded, so a real doc is never replaced. Nothing is written here, so it works
   * for viewers too; the server merge starts from the same seed when a writer's first edit arrives.
   */
  const { doc, error, isError } = useYDoc(isCollabEntity(entity) ? entity : { ...entity, yDoc: getSeedYDocString() })
  const webSocket = useWebSocket()

  const { profile } = graphHooks.useProfile()
  const { color } = getProfileColor(profile)

  if (isError) {
    logger.error('Error loading Y.Doc', error)
    return <AlertError error={error}>Unable to load the document</AlertError>
  }

  return entity && doc ? (
    <CollabWebsocketEditor
      key={entity.id}
      {...restProps}
      readOnly={Boolean(readOnly || props.readOnly)}
      name={profile?.name ?? profile?.email ?? 'Anonymous'}
      color={color}
      webSocket={webSocket}
      doc={doc}
      element={entity}
    />
  ) : null
}

export default CollabWebsocketEditor
